import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Heart } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useMissions } from '@/hooks/useMissions';
import { 
  likeGame, 
  unlikeGame, 
  isGameLiked, 
  getGameLikesCount 
} from '@/services/likesService';
import { toast } from 'sonner';

interface GameLikeButtonProps {
  gameId: string;
  className?: string;
}

export function GameLikeButton({ gameId, className }: GameLikeButtonProps) {
  const { user } = useAuth();
  const { handleLikeGame } = useMissions();
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id && gameId) {
      loadLikeStatus();
    }
  }, [user?.id, gameId]);

  const loadLikeStatus = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const [liked, count] = await Promise.all([
        isGameLiked(gameId, user.id),
        getGameLikesCount(gameId)
      ]);
      
      setIsLiked(liked);
      setLikesCount(count);
    } catch (error) {
      console.error('Error loading like status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLike = async () => {
    if (!user) {
      toast.error('You must be logged in to like games');
      return;
    }

    // If already liked, don't allow unlike for players
    if (isLiked) {
      toast.info('You cannot unlike a game once you have liked it');
      return;
    }

    try {
      const success = await likeGame(gameId, user.id);
      if (success) {
        setIsLiked(true);
        setLikesCount(prev => prev + 1);
        toast.success('Game liked!');
        
        // Complete the like game mission
        handleLikeGame(gameId);
      } else {
        toast.error('Failed to like game');
      }
    } catch (error) {
      console.error('Error liking game:', error);
      toast.error('An error occurred');
    }
  };

  if (loading) {
    return (
      <Button variant="outline" size="sm" disabled className={className}>
        <Heart className="w-4 h-4 mr-2" />
        Loading...
      </Button>
    );
  }

  return (
    <Button
      variant={isLiked ? "default" : "outline"}
      size="sm"
      onClick={handleToggleLike}
      disabled={isLiked}
      className={`h-8 px-2 text-xs ${isLiked ? 'cursor-not-allowed !opacity-100' : ''} ${className}`}
    >
      <Heart 
        className={`w-3 h-3 mr-1 ${isLiked ? 'fill-current' : ''}`} 
      />
      {likesCount}
    </Button>
  );
}
